/**
 * The pieces of Acceso, the door to the restaurant (the login and its family: second factor, «Elegir dónde entrar»,
 * «Confirmando tu sesión», «Crea tu PIN», the invitation, the expired session). Built 1:1 with the mockup of
 * 17 October 2026 (`docs/audits/2026-10-17-acceso-cocina-servicio-plan/PLAN.md` §5) on the living foundation:
 * Motion only through `MotionProvider` and the `m` components, transform and opacity only, every change in its final
 * state under reduced motion.
 *
 * - No card around a form: each field is its own white pane on the paper. The label sits inside the field and springs
 *   up when the field is focused or filled; focus answers with a terracotta ring that springs in.
 * - The password's «Mostrar» is an eye that morphs; its accessible name stays «Mostrar» / «Ocultar».
 * - A code is six boxes over one real input; a new PIN is six slots over one real input.
 * - Bands carry text only when something is wrong, in the Service's words.
 */

import { AnimatePresence } from './living/presence';
import { m } from './living/elements';
import { useId, useState } from 'react';
import type { CSSProperties, ReactNode, Ref } from 'react';

import { riseIn } from './living/enter';
import { SPRING, useLivingStill } from './living/motion-provider';
import { StaffAvatar } from './staff-avatar';
import './acceso.scss';

/* ── Motion helpers ─────────────────────────────────────────────────────────────────────────────────────────── */

const WEB_ANIMATIONS = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';

/**
 * True when nothing of Acceso may move: reduced motion, the page asked to stand still (`data-vv-still`), or a document
 * without the Web Animations API (nothing to hand opacity and transform to). Every piece then lands in its final state
 * on its first render, with no arrival and no exit.
 */
export function useDoorStill(): boolean {
  return useLivingStill() || !WEB_ANIMATIONS;
}

/** The entrance stagger of a door: 45 ms between rows, 18 px of rise (the foundation's `riseIn`, a little slower). */
export const DOOR_STEP = { step: 0.045, y: 18 } as const;

/**
 * One row of a door's column. It rises once with the entrance stagger and, with `layout`, slides when a band arrives
 * above it (Motion writes the slide as a transform).
 */
export function Row({ index, children, className, id, rises = true }: {
  readonly index: number;
  readonly children: ReactNode;
  readonly className?: string;
  readonly id?: string;
  /** False: the row is simply there (the brand row a travelling mark lands on). */
  readonly rises?: boolean;
}) {
  const still = useDoorStill();
  const rise = riseIn(index, DOOR_STEP);
  return (
    <m.div animate={rise.animate} className={className} id={id} initial={rises && !still ? rise.initial : false} layout="position"
      onUpdate={rise.onUpdate} transition={{ ...rise.transition, layout: SPRING.soft }}>
      {children}
    </m.div>
  );
}

/** A band, a note or a pill that arrives and leaves inside an `AnimatePresence`: it slides 10 px and fades. */
export function Arrive({ children, className }: { readonly children: ReactNode; readonly className?: string }) {
  const still = useDoorStill();
  return (
    <m.div animate={{ opacity: 1, y: 0 }} className={className} {...(still ? {} : { exit: { opacity: 0, y: -6, transition: { duration: 0.16 } } })}
      initial={still ? false : { opacity: 0, y: -10 }} layout="position" onUpdate={keepTogether} transition={{ ...SPRING.snap, layout: SPRING.soft }}>
      {children}
    </m.div>
  );
}

/* Keeps every value of an element that also carries `layout` on the script's clock (see the living README). */
const keepTogether = (): void => undefined;

/** The refusal shake of a form: once, transform only. */
export const SHAKE = ['translateX(0px)', 'translateX(-11px)', 'translateX(9px)', 'translateX(-6px)', 'translateX(3px)', 'translateX(0px)'];
/** The repeat PIN's shake when it does not match. */
export const SHAKE_SMALL = ['translateX(0px)', 'translateX(-9px)', 'translateX(7px)', 'translateX(-4px)', 'translateX(0px)'];

/**
 * Today's dialogs are `aria-modal="true"`; the living card does not set it. A ref on the card's first child marks its
 * dialog as it mounts (Radix's open-focus hook does not run when a control inside has already focused itself).
 */
export function markModal(node: HTMLElement | null): void {
  node?.closest('[role="dialog"]')?.setAttribute('aria-modal', 'true');
}

/* ── Line drawings ──────────────────────────────────────────────────────────────────────────────────────────── */

const STROKE = { fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 1.7 } as const;

/** `crossed`: the password is visible, so the button offers to hide it («Ocultar»). */
function EyeMorph({ crossed }: { readonly crossed: boolean }) {
  return (
    <svg aria-hidden="true" className="acc-eye" focusable="false" viewBox="0 0 24 24" {...STROKE}>
      <path d="M2.6 12c2.1-3.9 5.3-6.2 9.4-6.2s7.3 2.3 9.4 6.2c-2.1 3.9-5.3 6.2-9.4 6.2S4.7 15.9 2.6 12Z" />
      <m.circle animate={{ scale: crossed ? 0.62 : 1 }} cx="12" cy="12" initial={false} r="3" style={{ transformOrigin: '12px 12px' }}
        transition={SPRING.snap} />
      {/* The slash cuts the eye with a gap of the field's own white, then draws its line over it. */}
      <m.path animate={{ pathLength: crossed ? 1 : 0, opacity: crossed ? 1 : 0 }} className="acc-eye__gap" d="M4.6 4.6 19.4 19.4"
        initial={false} strokeWidth={4.6} transition={SPRING.snap} />
      <m.path animate={{ pathLength: crossed ? 1 : 0, opacity: crossed ? 1 : 0 }} d="M4.6 4.6 19.4 19.4" initial={false}
        transition={SPRING.snap} />
    </svg>
  );
}

/** A tick that draws itself (a primary action that succeeded, a confirmed change). */
export function DrawnTick({ className, delay = 0 }: { readonly className?: string; readonly delay?: number }) {
  const still = useDoorStill();
  return (
    <svg aria-hidden="true" className={className ?? 'acc-tick'} focusable="false" viewBox="0 0 24 24" {...STROKE} strokeWidth={2.2}>
      <m.path animate={{ pathLength: 1, opacity: 1 }} d="M5 12.6 9.6 17 19 7.4" initial={still ? false : { pathLength: 0, opacity: 0 }}
        transition={{ duration: 0.42, delay, ease: [0.3, 0.7, 0.2, 1] }} />
    </svg>
  );
}

export function ArrowRight({ className }: { readonly className?: string }) {
  return (
    <svg aria-hidden="true" className={className} focusable="false" viewBox="0 0 24 24" {...STROKE}>
      <path d="M5 12h13.5M13 6.5l5.5 5.5-5.5 5.5" />
    </svg>
  );
}

function Backspace() {
  return (
    <svg aria-hidden="true" className="acc-key__glyph" focusable="false" viewBox="0 0 24 24" {...STROKE}>
      <path d="M9.2 5.5h9.3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9.2L3.5 12l5.7-6.5Z" />
      <path d="m11.8 9.6 4.8 4.8m0-4.8-4.8 4.8" />
    </svg>
  );
}

export type BandTone = 'critical' | 'caution' | 'info' | 'positive';

/** The Panel's semantic glyphs, drawn on the same 20 grid, so a band reads as today's. */
function Glyph({ tone }: { readonly tone: BandTone }) {
  return (
    <svg aria-hidden="true" className="acc-glyph" focusable="false" viewBox="0 0 20 20" {...STROKE} strokeWidth={1.6}>
      {tone === 'critical' && <><circle cx="10" cy="10" r="7" /><path d="m7.5 7.5 5 5m0-5-5 5" /></>}
      {tone === 'caution' && <><path d="M10 3 18 17H2L10 3Z" /><path d="M10 8v4m0 2.5v.1" /></>}
      {tone === 'info' && <><circle cx="10" cy="10" r="7" /><path d="M10 9v5m0-8v.1" /></>}
      {tone === 'positive' && <><circle cx="10" cy="10" r="7" /><path d="m6.5 10 2.2 2.2 4.8-5" /></>}
    </svg>
  );
}

/** This tablet, on the same 20 grid (docs/decisions/2026-10-28-tpv-sin-conexion.md: the login door's band). */
function TabletGlyph() {
  return (
    <svg aria-hidden="true" className="acc-glyph acc-glyph--tablet" focusable="false" viewBox="0 0 20 20" {...STROKE} strokeWidth={1.6}>
      <rect height="15" rx="2.1" width="12.5" x="3.75" y="2.5" /><path d="M9.2 15h1.6" />
    </svg>
  );
}

/* ── Bands ──────────────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The one-line bands of the access family. Critical for a refusal, caution for a lost connection, info for a state the
 * person waits on. A waiting band has a breathing dot instead of a glyph: the same breath as the mark.
 */
export function Band({ tone, children, role, id, action, breathing = false, className, glyph }: {
  readonly tone: BandTone;
  /** `tablet`: the band speaks of this tablet (what it keeps unsent), drawn with a tablet instead of the tone's glyph. */
  readonly glyph?: 'tablet';
  readonly children: ReactNode;
  readonly role?: 'alert' | 'status';
  readonly id?: string;
  readonly action?: ReactNode;
  readonly breathing?: boolean;
  readonly className?: string;
}) {
  const still = useDoorStill();
  return (
    <div className={['acc-band', `acc-band--${tone}`, className ?? ''].filter(Boolean).join(' ')} id={id} role={role}>
      {breathing
        ? <m.i animate={still ? { opacity: 1 } : { opacity: [0.35, 1, 0.35], scale: [0.85, 1.1, 0.85] }} aria-hidden="true" className="acc-band__breath"
          transition={still ? { duration: 0 } : { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }} />
        : glyph === 'tablet' ? <TabletGlyph /> : <Glyph tone={tone} />}
      <p className="acc-band__text">{children}</p>
      {action}
    </div>
  );
}

/* ── The Exylon mark ────────────────────────────────────────────────────────────────────────────────────────── */

const BREATH = { duration: 2.8, repeat: Infinity, ease: 'easeInOut' } as const;

/**
 * The Exylon mark: the rail's own tile (a terracotta squircle with a Newsreader «e», `.app-navigation__brand::before`),
 * at any size with the same proportions (radius 0.325, letter 0.55), so that after «Entrar» it can travel to the rail
 * and land on the rail's tile exactly. `breathing`: the house is checking (a slow swell of the tile and a warm halo).
 */
export function ExylonMark({ size, breathing = false, hidden = false, markRef }: {
  readonly size: number;
  readonly breathing?: boolean;
  readonly hidden?: boolean;
  readonly markRef?: Ref<HTMLSpanElement>;
}) {
  const still = useDoorStill();
  const moving = breathing && !still;
  return (
    <span aria-hidden="true" className="acc-mark" data-hidden={hidden || undefined} ref={markRef} style={{ '--mark': `${size}px` } as CSSProperties}>
      {moving ? <m.i animate={{ opacity: [0.28, 0.85, 0.28], scale: [0.9, 1.08, 0.9] }} className="acc-mark__halo" initial={{ opacity: 0.28, scale: 0.9 }}
        transition={BREATH} /> : breathing ? <i className="acc-mark__halo acc-mark__halo--still" /> : null}
      <m.span animate={moving ? { scale: [1, 1.045, 1] } : { scale: 1 }} className="acc-mark__tile" initial={false}
        transition={moving ? BREATH : { duration: 0.3 }}>e</m.span>
    </span>
  );
}

export interface DoorPerson {
  readonly name: string;
  readonly avatarUrl?: string | null | undefined;
}

const initialOf = (name: string): string => Array.from(name.trim())[0]?.toLocaleUpperCase('es-ES') ?? '·';

/** The mark and the context beside it («Exylon» before anyone has entered; the restaurant once it is known). */
export function Brand({ context, breathing = false, markRef, hideMark = false, person }: {
  readonly context: string;
  readonly breathing?: boolean;
  readonly markRef?: Ref<HTMLSpanElement>;
  readonly hideMark?: boolean;
  readonly person?: DoorPerson | undefined;
}) {
  return (
    <div className="acc-brand">
      <ExylonMark breathing={breathing} hidden={hideMark} {...(markRef === undefined ? {} : { markRef })} size={46} />
      <span className="acc-brand__context">{context}</span>
      {person !== undefined && <span className="acc-brand__who">
        <StaffAvatar className="acc-person__face acc-person__face--small" fallback={initialOf(person.name)} imageUrl={person.avatarUrl} />
        <span>{person.name}</span>
      </span>}
    </div>
  );
}

/* ── The photograph ─────────────────────────────────────────────────────────────────────────────────────────── */

/** @internal Exported for its spec. The owner's picture of the pass before service (1122 × 1402, WebP). */
export const HERO_SRC = '/redesign/acceso/acceso-hero.webp';

/**
 * The photographic hero: the pane fades in while what it shows settles from a slight zoom (one soft spring). A plain
 * warm surface sits under it while it loads, and stays in its place if it ever fails.
 */
export function Hero() {
  const still = useDoorStill();
  const [state, setState] = useState<'loading' | 'shown' | 'absent'>('loading');
  return (
    <m.div animate={{ opacity: 1 }} aria-hidden="true" className="acc-hero__pane" initial={still ? false : { opacity: 0 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}>
      <m.div animate={{ transform: 'scale(1)' }} className="acc-hero__media" initial={still ? false : { transform: 'scale(1.06)' }}
        transition={{ ...SPRING.soft, visualDuration: 0.9 }}>
        <div className="acc-hero__tint" />
        {state !== 'absent' && (
          <img alt="" className="acc-hero__photo" data-shown={state === 'shown'} decoding="async" draggable={false}
            onError={() => setState('absent')} onLoad={() => setState('shown')} src={HERO_SRC} />
        )}
      </m.div>
    </m.div>
  );
}

/* ── Fields ─────────────────────────────────────────────────────────────────────────────────────────────────── */

export interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'email' | 'password';
  readonly inputMode?: 'text' | 'email' | 'numeric' | 'none';
  readonly autoComplete?: string;
  readonly placeholder?: string;
  readonly invalid?: boolean;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  readonly testId?: string;
  readonly sourceControl?: string;
  readonly name?: string;
  readonly maxLength?: number;
  readonly minLength?: number;
  readonly required?: boolean;
  readonly trailing?: ReactNode;
  readonly onFocusChange?: (focused: boolean) => void;
  readonly inputRef?: Ref<HTMLInputElement>;
  readonly className?: string;
}

export function Field({ label, value, onChange, type = 'text', inputMode, autoComplete = 'off', placeholder, invalid = false,
  describedBy, disabled = false, autoFocus = false, testId, sourceControl, name, maxLength, minLength, required, trailing,
  onFocusChange, inputRef, className }: FieldProps) {
  const id = useId();
  const [focused, setFocused] = useState(autoFocus);
  const raised = focused || value !== '';
  return (
    <div className={['acc-field', className ?? ''].filter(Boolean).join(' ')} data-disabled={disabled || undefined} data-focused={focused || undefined}
      data-invalid={invalid || undefined} data-raised={raised || undefined}>
      <label className="acc-field__label" htmlFor={id}>{label}</label>
      <input aria-describedby={describedBy} aria-invalid={invalid} autoComplete={autoComplete} autoFocus={autoFocus}
        className="acc-field__input" data-source-control={sourceControl} data-testid={testId} disabled={disabled} id={id}
        inputMode={inputMode} maxLength={maxLength} minLength={minLength} name={name}
        onBlur={() => { setFocused(false); onFocusChange?.(false); }}
        onChange={(event) => onChange(event.currentTarget.value)}
        onFocus={() => { setFocused(true); onFocusChange?.(true); }}
        placeholder={placeholder} ref={inputRef} required={required} spellCheck={false} type={type} value={value} />
      {trailing}
    </div>
  );
}

export interface PasswordFieldProps extends Omit<FieldProps, 'type' | 'trailing'> {
  readonly visible: boolean;
  readonly onToggle: () => void;
  readonly toggleTestId?: string;
  readonly toggleSourceControl?: string;
  readonly toggleDisabled?: boolean;
}

/** A secret with the morphing eye. Pressing the eye keeps the caret in the field. */
export function PasswordField({ visible, onToggle, toggleTestId, toggleSourceControl, toggleDisabled = false, ...field }: PasswordFieldProps) {
  return (
    <Field {...field} className="acc-field--secret" trailing={
      <button aria-label={visible ? 'Ocultar' : 'Mostrar'} aria-pressed={visible} className="acc-field__eye lv-press"
        data-source-control={toggleSourceControl} data-testid={toggleTestId} disabled={toggleDisabled} onClick={onToggle}
        onMouseDown={(event) => event.preventDefault()} type="button">
        <EyeMorph crossed={visible} />
      </button>
    } type={visible ? 'text' : 'password'} />
  );
}

/** Six boxes for the second factor. The real input lies over them, invisible; the boxes draw its value. */
export function CodeBoxes({ value, onChange, disabled = false, invalid = false, autoFocus = true, label = 'Código de verificación',
  describedBy, onFocusChange }: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly autoFocus?: boolean;
  readonly label?: string;
  readonly describedBy?: string | undefined;
  readonly onFocusChange?: (focused: boolean) => void;
}) {
  const id = useId();
  const still = useDoorStill();
  const [focused, setFocused] = useState(autoFocus);
  const active = Math.min(value.length, 5);
  return (
    <div className="acc-code" data-invalid={invalid || undefined}>
      <label className="acc-code__label" htmlFor={id}>{label}</label>
      <div className="acc-code__boxes">
        {Array.from({ length: 6 }, (_, index) => {
          const digit = value[index];
          return (
            <m.span animate={{ opacity: 1, transform: 'translateX(0px)' }} aria-hidden="true" className="acc-code__box"
              data-active={focused && index === active ? 'true' : undefined} data-filled={digit !== undefined || undefined} key={index}
              initial={still ? false : { opacity: 0, transform: 'translateX(18px)' }} transition={{ ...SPRING.snap, delay: index * 0.028 }}>
              <AnimatePresence initial={false}>
                {digit !== undefined && (
                  <m.span animate={{ opacity: 1, transform: 'scale(1)' }} className="acc-code__digit" {...(still ? {} : { exit: { opacity: 0, transform: 'scale(0.6)' } })}
                    initial={still ? false : { opacity: 0, transform: 'scale(0.55)' }} key={digit + index} transition={SPRING.pop}>{digit}</m.span>
                )}
              </AnimatePresence>
            </m.span>
          );
        })}
        <input aria-describedby={describedBy} aria-invalid={invalid} autoComplete="one-time-code" autoFocus={autoFocus}
          className="acc-code__input" disabled={disabled} id={id} inputMode="numeric" maxLength={6} name="secondFactor"
          onBlur={() => { setFocused(false); onFocusChange?.(false); }}
          onChange={(event) => onChange(event.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
          onFocus={() => { setFocused(true); onFocusChange?.(true); }}
          pattern="[0-9]{6}" required value={value} />
      </div>
    </div>
  );
}

/** A new PIN of six digits (ruling 40 of the 31 October architecture plan). The six slots say the rule. */
/** The length of a new staff PIN; the backend contract refuses any other. */
export const NEW_PIN_DIGITS = 6;

export function PinSlots({ label, value, onChange, invalid = false, disabled = false, matched = false, describedBy, onFocus,
  inputRef, inputMode = 'numeric' }: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly invalid?: boolean;
  readonly disabled?: boolean;
  readonly matched?: boolean;
  readonly describedBy?: string | undefined;
  readonly onFocus?: () => void;
  readonly inputRef?: Ref<HTMLInputElement>;
  readonly inputMode?: 'numeric' | 'none';
}) {
  const id = useId();
  const still = useDoorStill();
  const [focused, setFocused] = useState(false);
  return (
    <div className="acc-pin" data-focused={focused || undefined} data-invalid={invalid || undefined}>
      <label className="acc-pin__label" htmlFor={id}>{label}</label>
      <div className="acc-pin__row">
        <div aria-hidden="true" className="acc-pin__slots">
          {Array.from({ length: NEW_PIN_DIGITS }, (_, index) => (
            <span className="acc-pin__slot" data-active={focused && index === Math.min(value.length, NEW_PIN_DIGITS - 1) ? 'true' : undefined}
              data-filled={index < value.length || undefined} key={index}>
              <AnimatePresence initial={false}>
                {index < value.length && (
                  <m.i animate={{ opacity: 1, transform: 'scale(1)' }} className="acc-pin__dot" {...(still ? {} : { exit: { opacity: 0, transform: 'scale(0.4)' } })}
                    initial={still ? false : { opacity: 0, transform: 'scale(0.3)' }} transition={SPRING.pop} />
                )}
              </AnimatePresence>
            </span>
          ))}
        </div>
        <span className="acc-pin__ok">{matched && <DrawnTick className="acc-pin__tick" />}</span>
        <input aria-describedby={describedBy} aria-invalid={invalid} autoComplete="new-password" className="acc-pin__input"
          disabled={disabled} id={id} inputMode={inputMode} maxLength={NEW_PIN_DIGITS}
          onBlur={() => setFocused(false)} onChange={(event) => onChange(event.currentTarget.value)}
          onFocus={() => { setFocused(true); onFocus?.(); }} pattern="[0-9]{6}" ref={inputRef} required type="password" value={value} />
      </div>
    </div>
  );
}

/** Today's keypad (1–9, a blank, 0, «⌫»), as large quiet keys. It writes into the last PIN field that had focus. */
export function Keypad({ onDigit, onBackspace, disabled = false }: {
  readonly onDigit: (digit: string) => void;
  readonly onBackspace: () => void;
  readonly disabled?: boolean;
}) {
  return (
    <div aria-label="Teclado numérico del PIN nuevo" className="acc-keypad" role="group">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key) => key === '' ? <span aria-hidden="true" key="space" />
        : (
          <button aria-label={key === '⌫' ? 'Borrar último dígito' : key} className="acc-key lv-press" disabled={disabled} key={key}
            onClick={() => (key === '⌫' ? onBackspace() : onDigit(key))} onMouseDown={(event) => event.preventDefault()} type="button">
            {key === '⌫' ? <Backspace /> : key}
          </button>
        ))}
    </div>
  );
}

/* ── The main action ────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The label of a primary action, changed in place (no layout jump): «Entrar» → «Comprobando…» → a drawn tick. The
 * accessible name is the visually hidden text; the moving label is decoration.
 */
export function ActionLabel({ text, tick = false }: { readonly text: string; readonly tick?: boolean }) {
  const still = useDoorStill();
  const key = tick ? 'tick' : text;
  return (
    <>
      <span className="acc-sr">{text}</span>
      <AnimatePresence initial={false} mode="popLayout">
        <m.span animate={{ opacity: 1, transform: 'translateY(0px)' }} aria-hidden="true" className="acc-submit__label"
          {...(still ? {} : { exit: { opacity: 0, transform: 'translateY(-12px)' } })} initial={still ? false : { opacity: 0, transform: 'translateY(12px)' }} key={key}
          transition={SPRING.snap}>
          {tick ? <DrawnTick className="acc-submit__tick" delay={0.05} /> : text}
        </m.span>
      </AnimatePresence>
    </>
  );
}

/** The person whose door this is, with their face (the initial when there is no picture). */
export function PersonFace({ person, className }: { readonly person: DoorPerson; readonly className: string }) {
  return <StaffAvatar className={className} fallback={initialOf(person.name)} imageUrl={person.avatarUrl} />;
}
